import { useState, useEffect } from "react";
import Position from "../types/Position";
import {
  addNewPosition,
  getAllPositions,
  updatePosition as updatePositionAPI,
  deletePosition as deletePositionAPI,
} from "../services/definitionsService";

export function usePositions() {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const positionsData = await getAllPositions();
      setPositions(positionsData);
    } catch (err) {
      setError("Failed to fetch positions.");
    } finally {
      setLoading(false);
    }
  };

  const addPosition = async (title: string, summary: string) => {
    const newPos: Position = {
      positionId: "", // let backend generate ID
      name: title,
      summary: summary,
    };

    try {
      const response = await addNewPosition(newPos); // backend creates and returns new position
      const saved = response.position;
      const formattedResponse = {
        positionId: saved.positionId,
        name: saved.name,
        summary: saved.summary,
      };
      setPositions((prev) => [...prev, formattedResponse]);
      return saved;
    } catch (err) {
      console.error("Failed to add position:", err);
      setError("Failed to add position.");
      throw err;
    }
  };

  const updatePosition = async (id: string, title: string, summary: string) => {
    const updatedPos: Position = {
      positionId: id,
      name: title,
      summary: summary,
    };

    try {
      const response = await updatePositionAPI(updatedPos); // backend updates and returns saved item
      const saved = response.position;
      const formattedResponse = {
        positionId: saved.positionId,
        name: saved.name,
        summary: saved.summary,
      };
      setPositions((prev) =>
        prev.map((pos) => (pos.positionId === id ? formattedResponse : pos))
      );
    } catch (err) {
      console.error("Failed to update position:", err);
      setError("Failed to update position.");
    }
  };

  const deletePosition = async (id: string) => {
    try {
      await deletePositionAPI(id);
      setPositions((prev) => prev.filter((pos) => pos.positionId !== id));
    } catch (err: any) {
      console.error("Failed to delete position:", err);
      setError("Failed to delete position: " + err.response.data.error);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  return {
    positions,
    loading,
    error,
    fetchPositions,
    addPosition,
    updatePosition,
    deletePosition,
  };
}
